import { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useNavigate } from "react-router-dom";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { ProgramRole, User, TraineeType } from "../../API";
import {
  selectCurrentUser,
  selectUserRoles
} from "../Users/usersSlice";
import { Role } from "../Auth/roles";
import { useAppSelector } from "../../app/hooks";
import { selectFeaturesForProgram } from "../Networks/networkFeaturesSlice";
import { useLoadFeaturesForProgram } from "../Networks/networkFeaturesUtils";
import {
  InvitationFormErrors,
  InvitationNetworkErrors,
  useCreateUserInvitation,
  useSendUserInvitation
} from "./userInvitationUtils";
import {
  useCreateUser,
  useDeleteUser,
  useUsersByEmail,
  useUsersByNPI
} from "../Users/userUtils";
import {
  InstitutionNotifications,
  alphabetizeInstitutions
} from "../Institutions/institutionUtils";
import {
  ProgramNetworkErrors,
  ProgramNotifications,
  alphabetizePrograms,
  useCreateProgramMembership
} from "../Programs/programUtils";
import {
  ExistingUserLookupErrors,
  ProgramRoleSelectFields,
  TraineeTypeSelectFields,
  Regex,
  UserFormErrors,
  UserNetworkErrors
} from "../../app/constants";
import { getProgramsCurrentUserCanAdminister } from "../../app/utils";
import { useTrainingStages } from "../TrainingStages/trainingStagesAPI";
import { AddUserToProgram } from "../Users/AddUserToProgram";
import { useDispatch } from "react-redux";
import { setModalContent, setShowModal } from "../../common-ui/modalSlice";
import { CreateUserInput } from "../Users/usersAPI";
import { useNetworkIdentifierConfig } from "../Networks/networkUtils";
import {
  IdentifierConfigStatus,
  IdentifierFields
} from "../Networks/IdentifierFields";
import {
  IDENTIFIER_KEY_LEARN,
  IDENTIFIER_KEY_NPI,
  IdentifierValues
} from "../Networks/identifierUtils";
import { create } from "lodash";

type NewUserInvitationFormInputs = {
  firstName: string;
  lastName: string;
  email: string;
  institutionId: string;
  programId: string;
  role: NewProgramRole;
  identifiers: IdentifierValues;
  pgy: number | null;
  yearInStage: number | null;
  traineeType: TraineeType | null;
  isClinicianTrainee: boolean;
  jobTitle: string | null;
};

const FELLOWSHIP_STAGE_CODE = "FELLOWSHIP";
const PGY_MIN = 1;
const PGY_MAX = 12;

export enum NewProgramRole {
  Clinician = "Clinician",
  ProgramAdmin = "ProgramAdmin",
  Admin = "Admin",
  // SE-1765: eligibility-only roles. Land as `programRole=Coach` or
  // `programRole=RotationDirector` on the created programmembership;
  // no NPI/PGY/traineeType fields — this shape is "just an invitation
  // to be picked as a coach or director on the corresponding admin
  // page." Offered only when the target program's network has the
  // matching feature flag (see the option gate below).
  Coach = "Coach",
  RotationDirector = "RotationDirector"
}

const pageDetails: PageWrapperProps = {
  className: "NewUserInvitation",
  headerText: "New User Invitation"
};

export const NewUserInvitation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentUsersRoles = useAppSelector(selectUserRoles);

  const [existingUsersWithEmail, setExistingUsersWithEmail] = useState<
    User[] | null
  >(null);
  // Identifiers sent with the last submit (configured keys only), handed to the
  // "Add existing user" dialog so none are dropped.
  const [submittedIdentifiers, setSubmittedIdentifiers] =
    useState<IdentifierValues>({});
  const [existingUsersWithNPI, setExistingUsersWithNPI] = useState<
    User[] | null
  >(null);

  const {
    programs: programsCurrentUserCanAdminister,
    institutions: institutionsCurrentUserCanAdminister
  } = getProgramsCurrentUserCanAdminister();

  const canAdministerMultiplePrograms =
    programsCurrentUserCanAdminister &&
    programsCurrentUserCanAdminister.length > 1;

  const canAdministerMultipleInstitutions =
    institutionsCurrentUserCanAdminister &&
    institutionsCurrentUserCanAdminister.length > 1;

  const isProgramAdmin =
    currentUsersRoles && currentUsersRoles.includes(Role.ProgramAdmin);

  const missingNeededProgram =
    !programsCurrentUserCanAdminister ||
    programsCurrentUserCanAdminister.length < 1;

  const missingNeededInstitution =
    !institutionsCurrentUserCanAdminister ||
    institutionsCurrentUserCanAdminister.length < 1;

  const {
    newUser,
    status: createUserStatus,
    error: createUserError,
    createUser
  } = useCreateUser();
  const alphabetizedInstitutions = institutionsCurrentUserCanAdminister
    ? alphabetizeInstitutions(institutionsCurrentUserCanAdminister)
    : institutionsCurrentUserCanAdminister;

  const {
    users: usersWithEmail,
    status: usersByEmailStatus,
    error: usersByEmailError,
    getUsersByEmail
  } = useUsersByEmail();
  const {
    users: usersWithNPI,
    status: usersByNPIStatus,
    error: usersByNPIError,
    getUsersByNPI
  } = useUsersByNPI();

  /** Form Creation */
  const defaultInstitutionId =
    institutionsCurrentUserCanAdminister && canAdministerMultipleInstitutions
      ? ""
      : institutionsCurrentUserCanAdminister?.[0]?.id;

  const defaultProgramId =
    programsCurrentUserCanAdminister && canAdministerMultiplePrograms
      ? ""
      : programsCurrentUserCanAdminister?.[0]?.id;

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    watch,
    setError,
    setValue,
    clearErrors,
    formState: { errors, isDirty }
  } = useForm<NewUserInvitationFormInputs>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      institutionId: defaultInstitutionId,
      programId: "",
      role: undefined,
      identifiers: {},
      pgy: undefined,
      yearInStage: undefined,
      traineeType: undefined,
      isClinicianTrainee: undefined,
      jobTitle: ""
    }
  });

  // Need to re-render the form when:
  // - role changes so that the correct role-dependant fields are shown.
  // - institution changes so that the program list is updated properly.
  const watchers = watch(["role", "institutionId"]);

  const selectedRole = watch("role");
  const isClinician = selectedRole === NewProgramRole.Clinician;
  const isClinicianTrainee = watch("isClinicianTrainee");
  const watchedProgramId = watch("programId");

  const { trainingStages } = useTrainingStages();
  const selectedProgramId = watchedProgramId || defaultProgramId;
  const selectedProgram = programsCurrentUserCanAdminister?.find(
    (p) => p.id === selectedProgramId
  );

  // SE-1765: per-program feature flags gate whether Coach /
  // RotationDirector appear in the role dropdown. `selectedProgramId`
  // may be empty if a caller with multiple programs hasn't picked one
  // yet; in that case the flags return "off" and the two roles are
  // hidden — appropriate, since they're program-scoped.
  //
  // For a SysAdmin (or any caller inviting into a program they aren't a
  // member of themselves), the initial `network-features` fetch won't
  // include the picked program — hence the on-demand hook: it fires on
  // every programId change, cache-first, and merges the flags into the
  // same `byProgramId` map the selector reads.
  useLoadFeaturesForProgram(selectedProgramId);
  const programFeatures = useAppSelector(
    selectFeaturesForProgram(selectedProgramId)
  );
  const stageForSelectedProgram = (selectedProgram as any)?.trainingStageId
    ? trainingStages.find(
        (s) => s.id === (selectedProgram as any).trainingStageId
      )
    : undefined;
  const isFellowshipTrainee =
    isClinicianTrainee &&
    stageForSelectedProgram?.code === FELLOWSHIP_STAGE_CODE;

  const mapNewRoleToProgramRole = (
    role: NewProgramRole | undefined,
    isClinicianTrainee: boolean
  ): ProgramRole | undefined => {
    if (role === NewProgramRole.Clinician) {
      return isClinicianTrainee ? ProgramRole.Trainee : ProgramRole.Attending;
    }
    if (role === NewProgramRole.ProgramAdmin) {
      return ProgramRole.ProgramAdmin;
    }
    if (role === NewProgramRole.Admin) {
      return ProgramRole.Admin;
    }
    if (role === NewProgramRole.Coach) {
      return ProgramRole.Coach;
    }
    if (role === NewProgramRole.RotationDirector) {
      return ProgramRole.RotationDirector;
    }
    // No role selected yet (or unrecognized) — callers handle undefined.
    // Must not throw: this runs during render/effects before a role is picked.
    return undefined;
  };

  const anyApiIsLoading = [usersByEmailStatus, usersByNPIStatus].includes(
    "loading"
  );
  const isSubmittingUser = createUserStatus === "loading";
  const isFormBusy = anyApiIsLoading || isSubmittingUser;

  const programsInSelectedInstitution =
    programsCurrentUserCanAdminister?.filter(
      (program) => program.institutionId === getValues("institutionId")
    );
  const alphabetizedProgramsInSelectedInstitution =
    programsInSelectedInstitution
      ? alphabetizePrograms(programsInSelectedInstitution)
      : programsInSelectedInstitution;

  // SE-3118: identifier fields come from the selected program's network config
  // for the resolved role, so any identifier type shows up without code changes.
  // No config → no identifier fields. selectedRole is undefined until the user
  // picks one.
  const resolvedProgramRole =
    selectedRole != null
      ? mapNewRoleToProgramRole(selectedRole, isClinicianTrainee)
      : undefined;
  const {
    config: identifierConfig,
    status: identifierStatus,
    getConfig: getIdentifierConfig
  } = useNetworkIdentifierConfig(selectedProgramId);
  // Until the config loads (or if it fails) we don't know which identifiers are
  // required, so the fields are replaced by a spinner / retry and Submit is off.
  const configLoading =
    !!selectedProgramId && identifierStatus === "loading";
  const configFailed = !!selectedProgramId && identifierStatus === "failed";
  // Identifier fields only appear for clinicians (see the render below).
  const identifierConfigPending =
    selectedRole === NewProgramRole.Clinician && (configLoading || configFailed);
  const roleIdentifiers =
    (resolvedProgramRole
      ? identifierConfig?.identifiersByRole?.[resolvedProgramRole]
      : undefined) ?? [];

  const onSubmit: SubmitHandler<NewUserInvitationFormInputs> = async (data) => {
    let deleteNewUser = false;
    const institutionIdToUse = data.institutionId ?? defaultInstitutionId;
    const programIdToUse = data.programId ?? defaultProgramId;

    if (currentUser && currentUser.id && institutionIdToUse) {
      // Check if the entered email is already in use
      const usersWithEmail = await getUsersByEmail(data.email);
      // Stop if the check failed; the hook's status is stale in this closure.
      if (usersWithEmail === undefined) {
        setError("email", {
          type: "custom",
          message: ExistingUserLookupErrors.EmailCheckFailed
        });
        return;
      }
      if (usersWithEmail && usersWithEmail.length > 0) {
        setError("email", {
          type: "custom",
          message: InvitationFormErrors.EmailInUseError
        });
      }
      // Only identifiers configured for this program + role are sent.
      const identifiers: IdentifierValues = {};
      for (const item of roleIdentifiers) {
        const value = String(data.identifiers?.[item.key] ?? "").trim();
        if (value) identifiers[item.key] = value;
      }
      setSubmittedIdentifiers(identifiers);
      // NPI stays an identity key: an NPI already in use opens "Add existing user".
      const npi = identifiers[IDENTIFIER_KEY_NPI];
      let usersWithNPI: User[] | null = null;
      if (npi) {
        const npiResult = await getUsersByNPI(Number(npi));
        if (npiResult === undefined) {
          setError(`identifiers.${IDENTIFIER_KEY_NPI}`, {
            type: "custom",
            message: ExistingUserLookupErrors.NpiCheckFailed
          });
          return;
        }
        usersWithNPI = npiResult;
        if (usersWithNPI && usersWithNPI.length > 0) {
          setError(`identifiers.${IDENTIFIER_KEY_NPI}`, {
            type: "custom",
            message: InvitationFormErrors.NpiInUseError
          });
        }
      }
      // Jump out if the npi or email is already in use.
      if (
        (usersWithEmail && usersWithEmail.length > 0) ||
        (usersWithNPI && usersWithNPI.length > 0)
      ) {
        setExistingUsersWithEmail(usersWithEmail);
        setExistingUsersWithNPI(usersWithNPI);
        return;
      }

      setExistingUsersWithEmail(null);
      setExistingUsersWithNPI(null);

      const userInput: CreateUserInput = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        identifiers,
        pgy: isFellowshipTrainee ? data.pgy : null,
        yearInStage: data.yearInStage,
        jobTitle: data.jobTitle,
        // role is required + validated before submit, so this is always defined
        programRole: mapNewRoleToProgramRole(
          data.role,
          data.isClinicianTrainee
        ) as ProgramRole,
        adminId: currentUser.id,
        programId: programIdToUse,
        startDate: new Date().toISOString(),
        traineeType:
          mapNewRoleToProgramRole(data.role, data.isClinicianTrainee) ===
          ProgramRole.Trainee
            ? data.traineeType
            : undefined
      };
      const createdUser = await createUser(userInput);
      // Log correctly
      console.log("createUser result:", {
        createdUser,
        createUserError,
        createUserStatus
      });

      if (createdUser && !createdUser.success && createdUser.identifierKey) {
        setError(`identifiers.${createdUser.identifierKey}`, {
          type: "custom",
          message: createdUser.error
        });
        return;
      }

      // Navigate based on return value (not hook state)
      if (createdUser?.success && createdUser.userId) {
        navigate(`/invitations/${createdUser.userId}`);
      }
    }
  };

  useEffect(() => {
    if (selectedRole !== NewProgramRole.Clinician) {
      // Clear the answer (not false) so re-selecting Clinician re-asks the
      // trainee question before revealing the applicable fields.
      setValue("isClinicianTrainee", undefined as unknown as boolean);
      setValue("yearInStage", null);
      setValue("pgy", null);
      setValue("traineeType", null);
      setValue("jobTitle", null);
      setValue("identifiers", {});
    }
  }, [selectedRole, setValue]);

  // The trainee answer flips the resolved role (Yes=Trainee, No=Attending), which
  // changes which identifiers are required. Clear any stale identifier errors on
  // that change so a leftover "required" from the previous role (e.g. LEARN
  // required for Attending) doesn't linger after switching to Trainee. They
  // re-validate on submit against the current config-driven rules.
  useEffect(() => {
    clearErrors("identifiers");
  }, [isClinicianTrainee, clearErrors]);

  // Clear stale PGY when the selected program/stage is no longer a fellowship.
  useEffect(() => {
    if (!isFellowshipTrainee) {
      setValue("pgy", null);
    }
  }, [isFellowshipTrainee, setValue]);

  // Automatically set the institutionId if the user only can administer within one institution
  useEffect(() => {
    reset({
      institutionId: defaultInstitutionId
    });
  }, [defaultInstitutionId]);

  // Watch for any api errors while checking for existing emails or npi
  useEffect(() => {
    if (usersByEmailError) {
      setError("email", {
        type: "custom",
        message: usersByEmailError
      });
    }
    if (usersByNPIError) {
      setError(`identifiers.${IDENTIFIER_KEY_NPI}`, {
        type: "custom",
        message: usersByNPIError
      });
    }
  }, [usersByEmailError, usersByNPIError]);

  useEffect(() => {
    if (
      (existingUsersWithEmail || existingUsersWithNPI) &&
      setShowModal &&
      setModalContent
    ) {
      const usersAreSame =
        Array.isArray(existingUsersWithEmail) &&
        Array.isArray(existingUsersWithNPI) &&
        existingUsersWithEmail.every((ue) =>
          existingUsersWithNPI.find((un) => un.id === ue.id)
        );

      dispatch(
        setModalContent(
          <AddUserToProgram
            existingUsersWithEmail={existingUsersWithEmail}
            existingUsersWithNPI={
              usersAreSame ? undefined : existingUsersWithNPI
            }
            selectedProgramRole={mapNewRoleToProgramRole(
              getValues("role"),
              getValues("isClinicianTrainee")
            )}
            selectedProgramTraineeType={getValues("traineeType") ?? undefined}
            selectedNPI={
              Number(submittedIdentifiers[IDENTIFIER_KEY_NPI]) || undefined
            }
            selectedInstitutionId={getValues("institutionId")}
            selectedProgramId={getValues("programId")}
            selectedSimplLearnId={submittedIdentifiers[IDENTIFIER_KEY_LEARN]}
            selectedIdentifiers={Object.fromEntries(
              Object.entries(submittedIdentifiers).filter(
                ([key]) =>
                  key !== IDENTIFIER_KEY_NPI && key !== IDENTIFIER_KEY_LEARN
              )
            )}
            identifierLabels={Object.fromEntries(
              roleIdentifiers.map((item) => [item.key, item.displayName])
            )}
            selectedYearInStage={getValues("yearInStage") ?? undefined}
            postSuccessAction={() => {
              dispatch(setShowModal(false));
              navigate("/users");
            }}
          />
        )
      );
      dispatch(setShowModal(true));
    }
  }, [existingUsersWithEmail, existingUsersWithNPI]);

  if (createUserStatus === "failed" || createUserError) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error")}</h2>
        <h3>{t(UserNetworkErrors.CreateUserError)}</h3>
        {createUserError && <p>{t(createUserError)}</p>}
      </PageWrapper>
    );
  }

  return (
    <PageWrapper {...pageDetails}>
      <Backdrop
        open={isSubmittingUser}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <form
        className="NewUserInvitationForm"
        onSubmit={handleSubmit(onSubmit)}
        aria-busy={isSubmittingUser}
        noValidate
        role="form"
      >
        <Grid
          container
          spacing={2}
          pt={2}
          xs={12}
          sm={11}
          md={11}
          lg={10}
          xl={9}
        >
          {/* ═══ Section: User Information ═══ */}
          <Grid xs={12}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 0.5 }}
            >
              {t("User Information")}
            </Typography>
          </Grid>

          <Grid xs={12} sm={6} md={4}>
            <Controller
              control={control}
              name="firstName"
              rules={{
                required: UserFormErrors.EmptyFirstName,
                maxLength: {
                  value: 40,
                  message: UserFormErrors.FirstNameTooLong
                }
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  size="small"
                  onChange={onChange}
                  label={t("First Name")}
                  onBlur={onBlur}
                  value={value ?? ""}
                  sx={{ width: "100%" }}
                  required
                  disabled={
                    isFormBusy ||
                    missingNeededProgram ||
                    missingNeededInstitution
                  }
                  error={!!errors.firstName?.message}
                  helperText={
                    errors.firstName?.message
                      ? t(errors.firstName.message)
                      : undefined
                  }
                />
              )}
            />
          </Grid>

          <Grid xs={12} sm={6} md={4}>
            <Controller
              control={control}
              name="lastName"
              rules={{
                required: UserFormErrors.EmptyLastName,
                maxLength: {
                  value: 40,
                  message: UserFormErrors.LastNameTooLong
                }
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  size="small"
                  onChange={onChange}
                  label={t("Last Name")}
                  onBlur={onBlur}
                  value={value ?? ""}
                  sx={{ width: "100%" }}
                  required
                  disabled={
                    isFormBusy ||
                    missingNeededProgram ||
                    missingNeededInstitution
                  }
                  error={!!errors.lastName?.message}
                  helperText={
                    errors.lastName?.message
                      ? t(errors.lastName.message)
                      : undefined
                  }
                />
              )}
            />
          </Grid>

          <Grid xs={12} sm={6} md={4}>
            <Controller
              control={control}
              name="email"
              rules={{
                required: UserFormErrors.EmptyEmail,
                pattern: {
                  value: Regex.Email,
                  message: UserFormErrors.InvalidEmail
                }
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  size="small"
                  onChange={onChange}
                  label={t("Email")}
                  onBlur={onBlur}
                  value={value ?? ""}
                  sx={{ width: "100%" }}
                  required
                  disabled={
                    isFormBusy ||
                    missingNeededProgram ||
                    missingNeededInstitution
                  }
                  error={!!errors.email?.message}
                  helperText={
                    errors.email?.message
                      ? t(errors.email.message)
                      : undefined
                  }
                />
              )}
            />
          </Grid>

          {/* ═══ Section: Program Membership ═══ */}
          <Grid xs={12} sx={{ mt: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 0.5 }}
            >
              {t("Program Membership")}
            </Typography>
          </Grid>

          <Grid xs={12} sm={6} md={4}>
            {canAdministerMultipleInstitutions ? (
              <Controller
                control={control}
                name="institutionId"
                rules={{
                  required: {
                    value: true,
                    message: InvitationFormErrors.InstitutionRequiredError
                  }
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <FormControl
                    fullWidth
                    size="small"
                    required
                    error={!!errors.institutionId?.message}
                    disabled={
                      isFormBusy ||
                      missingNeededProgram ||
                      missingNeededInstitution
                    }
                  >
                    <InputLabel id="invitation-institution-label">
                      {t("Institution")}
                    </InputLabel>
                    <Select
                      labelId="invitation-institution-label"
                      data-testid="invitation-institution-input-group"
                      id="institutionId"
                      value={value || ""}
                      label={t("Institution")}
                      onChange={onChange}
                      onBlur={onBlur}
                    >
                      <MenuItem value="" key="no-institution">
                        {t("(none)")}
                      </MenuItem>
                      {alphabetizedInstitutions?.map((institution, index) => (
                        <MenuItem
                          value={institution.id}
                          key={`${index}-${institution.id}`}
                        >
                          {institution.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.institutionId?.message && (
                      <FormHelperText>
                        {t(errors.institutionId.message)}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            ) : (
              <TextField
                size="small"
                label={t("Institution")}
                value={institutionsCurrentUserCanAdminister?.[0]?.name ?? ""}
                sx={{ width: "100%" }}
                disabled
                InputProps={{ readOnly: true }}
              />
            )}
          </Grid>

          <Grid xs={12} sm={6} md={4}>
            {canAdministerMultiplePrograms ? (
              <Controller
                control={control}
                name="programId"
                rules={{
                  required: {
                    value: true,
                    message: InvitationFormErrors.ProgramRequiredError
                  }
                }}
                render={({ field: { onChange, onBlur, value } }) => {
                  const programDisabled =
                    !getValues("institutionId") ||
                    isFormBusy ||
                    missingNeededProgram ||
                    missingNeededInstitution;
                  const noProgramsWarning =
                    !errors.programId?.message &&
                    !!getValues("institutionId") &&
                    (!alphabetizedProgramsInSelectedInstitution ||
                      alphabetizedProgramsInSelectedInstitution.length < 1);
                  return (
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!errors.programId?.message}
                      disabled={programDisabled}
                    >
                      <InputLabel id="invitation-program-label">
                        {t("Program")}
                      </InputLabel>
                      <Select
                        labelId="invitation-program-label"
                        data-testid="invitation-program-input-group"
                        id="programId"
                        value={value || ""}
                        label={t("Program")}
                        onChange={onChange}
                        onBlur={onBlur}
                      >
                        <MenuItem value="" key="no-program">
                          {t("(none)")}
                        </MenuItem>
                        {alphabetizedProgramsInSelectedInstitution?.map(
                          (program, index) => (
                            <MenuItem
                              value={program.id}
                              key={`${index}-${program.id}`}
                            >
                              {`${program.name} - (${program.institution?.name})`}
                            </MenuItem>
                          )
                        )}
                      </Select>
                      {errors.programId?.message && (
                        <FormHelperText>
                          {t(errors.programId.message)}
                        </FormHelperText>
                      )}
                      {noProgramsWarning && (
                        <FormHelperText>
                          {t(ProgramNotifications.NoProgramsInInstitution)}
                        </FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />
            ) : (
              <TextField
                size="small"
                label={t("Program")}
                value={programsCurrentUserCanAdminister?.[0]?.name ?? ""}
                sx={{ width: "100%" }}
                disabled
                InputProps={{ readOnly: true }}
              />
            )}
          </Grid>

          <Grid xs={12} sm={6} md={4}>
            <Controller
              control={control}
              name="role"
              rules={{ required: InvitationFormErrors.RoleRequiredError }}
              render={({ field: { onChange, onBlur, value } }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required
                  error={!!errors.role?.message}
                  disabled={
                    isFormBusy ||
                    missingNeededProgram ||
                    missingNeededInstitution
                  }
                >
                  <InputLabel id="invitation-role-label">
                    {t("Role")}
                  </InputLabel>
                  <Select
                    labelId="invitation-role-label"
                    data-testid="invitation-role-input-group"
                    id="role"
                    value={value || ""}
                    label={t("Role")}
                    onChange={onChange}
                    onBlur={onBlur}
                  >
                    <MenuItem value="">{t("(Select a role)")}</MenuItem>
                    {ProgramRoleSelectFields.map((programRole, index) => {
                      if (
                        isProgramAdmin &&
                        programRole.value === ProgramRole.Admin
                      )
                        return null;
                      // SE-1765: hide Coach / Rotation Director unless
                      // the selected program's network has the matching
                      // feature. When no program is picked yet, hide
                      // them too (they're program-scoped affordances,
                      // showing them before a program pins raises
                      // false expectations).
                      if (
                        programRole.value === ProgramRole.Coach &&
                        !programFeatures.coaching
                      )
                        return null;
                      if (
                        programRole.value === ProgramRole.RotationDirector &&
                        !programFeatures.rotations
                      )
                        return null;
                      return (
                        <MenuItem
                          value={programRole.value}
                          key={`${index}-${programRole.value}`}
                        >
                          {programRole.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {errors.role?.message && (
                    <FormHelperText>{t(errors.role.message)}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          {/* ═══ Section: Clinician Details (Role = Clinician) ═══ */}
          {isClinician && (
            <>
              <Grid xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 600, letterSpacing: 0.5 }}
                >
                  {t("Clinician Details")}
                </Typography>
              </Grid>

              {/* Trainee-vs-attending first — its answer determines which
                  identifier/detail fields apply below. */}
              <Grid xs={12}>
                <Controller
                  name="isClinicianTrainee"
                  control={control}
                  rules={{
                    validate: (value) =>
                      value !== undefined || "This field is required."
                  }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => (
                    <FormControl
                      component="fieldset"
                      error={!!error}
                      required
                      sx={{ width: "100%" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          columnGap: 2,
                          rowGap: 1
                        }}
                      >
                        <FormLabel
                          component="legend"
                          sx={{
                            fontSize: "1rem",
                            color: "text.primary"
                          }}
                        >
                          {t(
                            "Is this user a trainee in your program about whom you would like to collect assessments?"
                          )}
                        </FormLabel>
                        <RadioGroup
                          row
                          value={
                            value === undefined ? "" : value ? "yes" : "no"
                          }
                          onChange={(e) => {
                            onChange(e.target.value === "yes");
                          }}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio size="small" />}
                            label={t("Yes")}
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio size="small" />}
                            label={t("No")}
                          />
                        </RadioGroup>
                      </Box>
                      {error && (
                        <FormHelperText>{error.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* These fields live in the Clinician-only section above, so they
                  are gated on the trainee-vs-attending choice. Identifier config
                  for the ProgramAdmin role is NOT surfaced on this single-invite
                  form (NPI/LEARN are clinician identifiers); if a network ever
                  requires an identifier for ProgramAdmin, surface it via a
                  separate, deliberate change rather than here. */}
              {isClinicianTrainee !== undefined && (
              <>
              <Grid xs={12} sm={6} md={4}>
                <Controller
                  control={control}
                  name="jobTitle"
                  rules={{
                    required: { value: true, message: "Job Title is required" }
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      size="small"
                      onChange={onChange}
                      label={t("Job Title")}
                      onBlur={onBlur}
                      value={value ?? ""}
                      sx={{ width: "100%" }}
                      required
                      disabled={
                        isFormBusy ||
                        missingNeededProgram ||
                        missingNeededInstitution
                      }
                      error={!!errors.jobTitle?.message}
                      helperText={
                        errors.jobTitle?.message
                          ? t(errors.jobTitle.message)
                          : undefined
                      }
                    />
                  )}
                />
              </Grid>

              {configLoading || configFailed ? (
                <IdentifierConfigStatus
                  status={configLoading ? "loading" : "failed"}
                  onRetry={() => getIdentifierConfig(selectedProgramId)}
                />
              ) : (
                <IdentifierFields
                  items={roleIdentifiers}
                  control={control}
                  errors={errors}
                  disabled={
                    isFormBusy ||
                    missingNeededProgram ||
                    missingNeededInstitution
                  }
                />
              )}
              </>
              )}
            </>
          )}

          {/* ═══ Section: Trainee Details (isClinicianTrainee = true) ═══ */}
          {isClinicianTrainee && (
            <>
              <Grid xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 600, letterSpacing: 0.5 }}
                >
                  {t("Trainee Details")}
                </Typography>
              </Grid>

              <Grid xs={12} sm={6} md={4}>
                <Controller
                  control={control}
                  name="traineeType"
                  rules={{
                    required: {
                      value: isClinicianTrainee,
                      message: InvitationFormErrors.TraineeTypeRequiredError
                    }
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!errors.traineeType?.message}
                      disabled={
                        isFormBusy ||
                        missingNeededProgram ||
                        missingNeededInstitution
                      }
                    >
                      <InputLabel id="invitation-trainee-type-label">
                        {t("Trainee Type")}
                      </InputLabel>
                      <Select
                        labelId="invitation-trainee-type-label"
                        data-testid="invitation-trainee-type-input-group"
                        id="traineeType"
                        value={value || ""}
                        label={t("Trainee Type")}
                        onChange={onChange}
                        onBlur={onBlur}
                      >
                        {TraineeTypeSelectFields.map((traineeType, index) => (
                          <MenuItem
                            value={traineeType.value}
                            key={`${index}-${traineeType.label}`}
                          >
                            {traineeType.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.traineeType?.message && (
                        <FormHelperText>
                          {t(errors.traineeType.message)}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid xs={12} sm={6} md={4}>
                <Controller
                  control={control}
                  name="yearInStage"
                  rules={{
                    required: {
                      value: isClinicianTrainee,
                      message: "Clinical Training Year in Program is required for trainees"
                    }
                  }}
                  render={({ field: { onChange, onBlur, value } }) => {
                    const choices = stageForSelectedProgram
                      ? Array.from(
                          {
                            length:
                              stageForSelectedProgram.maxYear -
                              stageForSelectedProgram.minYear +
                              1
                          },
                          (_, i) => stageForSelectedProgram.minYear + i
                        )
                      : [];
                    const dropdownDisabled =
                      isFormBusy ||
                      missingNeededProgram ||
                      missingNeededInstitution ||
                      !stageForSelectedProgram;
                    const stageMissingNote =
                      !stageForSelectedProgram && isClinicianTrainee
                        ? t(
                            "Selected program has no training stage set; year cannot be entered yet."
                          )
                        : undefined;
                    return (
                      <FormControl
                        fullWidth
                        size="small"
                        required
                        error={!!errors.yearInStage?.message}
                        disabled={dropdownDisabled}
                      >
                        <InputLabel id="invitation-year-in-stage-label">
                          {t("Clinical Training Year in Program")}
                        </InputLabel>
                        <Select
                          labelId="invitation-year-in-stage-label"
                          data-testid="invitation-year-in-stage-input-group"
                          id="yearInStage"
                          value={value ?? ""}
                          label={t("Clinical Training Year in Program")}
                          onChange={onChange}
                          onBlur={onBlur}
                        >
                          {choices.map((year) => (
                            <MenuItem value={year} key={`year-${year}`}>
                              {String(year)}
                            </MenuItem>
                          ))}
                        </Select>
                        {(errors.yearInStage?.message ||
                          stageMissingNote) && (
                          <FormHelperText>
                            {errors.yearInStage?.message
                              ? t(errors.yearInStage.message)
                              : stageMissingNote}
                          </FormHelperText>
                        )}
                      </FormControl>
                    );
                  }}
                />
              </Grid>

              {isFellowshipTrainee && (
                <Grid xs={12} sm={6} md={4}>
                  <Controller
                    control={control}
                    name="pgy"
                    rules={{
                      required: {
                        value: isFellowshipTrainee,
                        message: "PGY is required for fellowship trainees"
                      },
                      min: {
                        value: PGY_MIN,
                        message: `PGY must be between ${PGY_MIN} and ${PGY_MAX}`
                      },
                      max: {
                        value: PGY_MAX,
                        message: `PGY must be between ${PGY_MIN} and ${PGY_MAX}`
                      }
                    }}
                    render={({ field: { onChange, onBlur, value } }) => {
                      const pgyChoices = Array.from(
                        { length: PGY_MAX - PGY_MIN + 1 },
                        (_, i) => PGY_MIN + i
                      );
                      return (
                        <FormControl
                          fullWidth
                          size="small"
                          required
                          error={!!errors.pgy?.message}
                          disabled={isFormBusy}
                        >
                          <InputLabel id="invitation-pgy-label">
                            {t("PGY")}
                          </InputLabel>
                          <Select
                            labelId="invitation-pgy-label"
                            data-testid="invitation-pgy-input-group"
                            id="pgy"
                            value={value ?? ""}
                            label={t("PGY")}
                            onChange={onChange}
                            onBlur={onBlur}
                          >
                            {pgyChoices.map((p) => (
                              <MenuItem value={p} key={`pgy-${p}`}>
                                {String(p)}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.pgy?.message && (
                            <FormHelperText>
                              {t(errors.pgy.message)}
                            </FormHelperText>
                          )}
                        </FormControl>
                      );
                    }}
                  />
                </Grid>
              )}
            </>
          )}

          {/* ═══ Submit / Cancel ═══ */}
          <Grid
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3
            }}
          >
            <Button
              disabled={isFormBusy}
              onClick={() => navigate("/users")}
              variant="outlined"
            >
              {t("Cancel")}
            </Button>
            <Button
              disabled={
                isFormBusy ||
                missingNeededProgram ||
                missingNeededInstitution ||
                identifierConfigPending
              }
              variant="contained"
              type="submit"
            >
              {t("Submit")}
            </Button>
          </Grid>
          {missingNeededProgram && (
            <Grid xs={12}>
              <Alert severity="warning" sx={{ marginTop: 1 }}>
                {t(InvitationFormErrors.NoProgramForCurrentUser)}
              </Alert>
            </Grid>
          )}
          {missingNeededInstitution && (
            <Grid xs={12}>
              <Alert severity="warning" sx={{ marginTop: 1 }}>
                {t(InvitationFormErrors.NoInstitutionForCurrentUser)}
              </Alert>
            </Grid>
          )}
        </Grid>
      </form>
    </PageWrapper>
  );
};
